import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useApps } from '@/contexts/AppsContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Brain,
  Boxes,
  Key,
  CreditCard,
  Settings,
  LogOut,
  ChevronDown,
  Plus,
  Activity,
  Users,
  Server,
  Upload,
  Database,
  FileText,
  Check,
  Lock,
  PanelLeftClose,
  PanelLeftOpen,
  Book,
  Shield,
  UserCircle,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// App-scoped nav — built dynamically with appId
const getAppNavItems = (appId) => [
  { title: 'Mind', href: `/apps/${appId}/mind`, icon: Brain },
  { title: 'Data', href: `/apps/${appId}/data`, icon: Database },
  { title: 'API Keys', href: `/apps/${appId}/keys`, icon: Key },
];

const getAdminExtraNav = (appId) => [
  { title: 'Ingest', href: `/apps/${appId}/ingest`, icon: Upload },
];

// Tenant-only global nav items
const tenantNavItems = [
  { title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { title: 'Apps', href: '/apps', icon: Boxes },
  { title: 'Settings', href: '/settings', icon: Settings },
  { title: 'Docs', href: '/documentation', icon: Book, external: true },
];

// Admin platform-level navigation
const adminPlatformNav = [
  { title: 'Platform Overview', href: '/admin', icon: Activity },
  { title: 'Apps', href: '/admin/tenants', icon: Users },
  { title: 'Service Catalog', href: '/admin/services', icon: Server },
];

// Onboarding wizard steps — must match CreateAppWizard STEPS exactly
const ONBOARDING_STEPS = [
  { label: 'Terms', icon: Shield, step: 0 },
  { label: 'Describe', icon: FileText, step: 1 },
  { label: 'Ownership', icon: UserCircle, step: 2 },
  { label: 'Schema', icon: Sparkles, step: 3 },
  { label: 'API Key', icon: Key, step: 4 },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { apps, currentApp, selectApp, appReadiness, checkAppReadiness } = useApps();
  const { logout, isAdmin } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true');

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  const readiness = currentApp ? appReadiness[currentApp.appId] : null;
  const isOnWizard = location.pathname === '/apps/new';

  // Select app and navigate to default screen
  const handleSelectApp = async (app) => {
    selectApp(app);
    // Use cached readiness if available, otherwise wait for check
    const cached = appReadiness[app.appId];
    let r;
    if (cached && !cached.loading) {
      r = cached;
    } else {
      try {
        r = await checkAppReadiness(app.appId);
      } catch {
        r = null;
      }
    }
    if (r && r.hasSchema && r.hasApiKey) {
      navigate(`/apps/${app.appId}/mind`);
    } else {
      const step = !r?.hasSchema ? 3 : 4;
      navigate(`/apps/new?step=${step}`);
    }
  };

  // Determine if app setup is complete
  const appIsComplete = currentApp && readiness && !readiness.loading && readiness.hasSchema && readiness.hasApiKey;
  const needsOnboarding = !appIsComplete && (isOnWizard || (currentApp && readiness && !readiness.loading));

  // Calculate the current onboarding step for the selected app
  const getMaxCompletedStep = () => {
    if (!readiness || readiness.loading) return -1;
    // Terms is tracked per-user, not per-app — assume accepted if they have an app
    // Step 0: Terms — done (they got past it to create the app)
    // Step 1: Describe — done (app exists with a name)
    // Step 2: Ownership — done (app has authorship model)
    if (!readiness.hasSchema) return 2; // Stuck at Schema (step 3)
    if (!readiness.hasApiKey) return 3; // Stuck at API Key (step 4)
    return 4; // All done
  };

  const maxCompletedStep = getMaxCompletedStep();

  // Render a nav link
  const renderNavLink = (item, extraClass = '') => {
    const isActive = location.pathname === item.href ||
      (item.href !== '/dashboard' && item.href !== '/admin' && location.pathname.startsWith(item.href + '/'));
    const className = cn(
      'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
      collapsed ? 'justify-center px-0' : 'space-x-3 px-3',
      isActive
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      extraClass,
    );
    const content = (
      <>
        <item.icon className="h-5 w-5 shrink-0" />
        {!collapsed && <span>{item.title}</span>}
      </>
    );

    if (item.external) {
      return (
        <a key={item.href} href={item.href} target="_blank" rel="noopener noreferrer" title={collapsed ? item.title : undefined} className={className}>
          {content}
        </a>
      );
    }

    return (
      <Link key={item.href} to={item.href} title={collapsed ? item.title : undefined} className={className}>
        {content}
      </Link>
    );
  };

  // App selector dropdown — shared between admin and tenant
  const renderAppSelector = () => (
    <div className={cn("border-b", collapsed ? 'p-2' : 'p-4')}>
      {!collapsed && (
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 px-1 font-semibold">
          {isAdmin ? 'App Selector' : 'Your App'}
        </p>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {collapsed ? (
            <Button variant="outline" size="icon" className="w-10 h-10 mx-auto flex" title={currentApp?.name || 'Select App'}>
              <span className="text-xs font-bold">{currentApp?.name?.[0] || '?'}</span>
            </Button>
          ) : (
            <Button variant="outline" className="w-full justify-between">
              <span className="truncate">
                {currentApp?.name || 'Select App'}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64" side={collapsed ? 'right' : undefined}>
          <DropdownMenuLabel>{isAdmin ? `All Apps (${apps.length})` : 'Your Apps'}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {apps.map((app) => (
            <DropdownMenuItem
              key={app.appId}
              onClick={() => handleSelectApp(app)}
              className={cn(
                'flex flex-col items-start',
                currentApp?.appId === app.appId && 'bg-accent'
              )}
            >
              <span className="truncate font-medium">{app.name}</span>
              {isAdmin && (
                <span className="text-[10px] text-muted-foreground truncate">
                  {app.adminEmail || 'No owner'}
                </span>
              )}
            </DropdownMenuItem>
          ))}
          {apps.length === 0 && (
            <DropdownMenuItem disabled>No apps yet</DropdownMenuItem>
          )}
          {!isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/apps/new" className="flex items-center">
                  <Plus className="mr-2 h-4 w-4" />
                  Create New App
                </Link>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

    </div>
  );

  // Onboarding nav — clickable wizard steps with progressive locking
  const renderOnboardingNav = () => {
    if (collapsed) return null;

    return (
      <div className="p-4 space-y-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5 px-1">
          Setup Progress
        </p>
        {ONBOARDING_STEPS.map((onbStep) => {
          const isCompleted = onbStep.step <= maxCompletedStep;
          const isCurrent = onbStep.step === maxCompletedStep + 1;
          const isLocked = onbStep.step > maxCompletedStep + 1;
          const Icon = onbStep.icon;

          if (isLocked) {
            return (
              <div
                key={onbStep.label}
                className="flex items-center gap-2 py-1.5 px-3 rounded-lg text-muted-foreground/40 cursor-not-allowed"
              >
                <Lock className="h-4 w-4 shrink-0" />
                <span className="text-xs">{onbStep.label}</span>
              </div>
            );
          }

          if (isCompleted) {
            return (
              <Link
                key={onbStep.label}
                to={`/apps/new?step=${onbStep.step}`}
                className="flex items-center gap-2 py-1.5 px-3 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-green-500/15">
                  <Check className="h-2.5 w-2.5 text-green-500" />
                </div>
                <span className="text-xs line-through">{onbStep.label}</span>
              </Link>
            );
          }

          // Current step
          return (
            <Link
              key={onbStep.label}
              to={`/apps/new?step=${onbStep.step}`}
              className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-accent text-accent-foreground transition-colors"
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="text-xs font-medium">{onbStep.label}</span>
            </Link>
          );
        })}
      </div>
    );
  };

  // Full app nav — shown when setup is complete
  const renderFullAppNav = () => (
    <div className={cn("space-y-1", collapsed ? 'p-2' : 'p-4')}>
      {getAppNavItems(currentApp.appId).map(item => renderNavLink(item))}
      {isAdmin && getAdminExtraNav(currentApp.appId).map(item => renderNavLink(item))}
      {/* Schema — app-specific route */}
      {currentApp && (
        <Link
          to={`/apps/${currentApp.appId}/schema`}
          title={collapsed ? 'Schema' : undefined}
          className={cn(
            'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
            collapsed ? 'justify-center px-0' : 'space-x-3 px-3',
            location.pathname.includes('/schema')
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
        >
          <FileText className="h-5 w-5 shrink-0" />
          {!collapsed && <span>Schema</span>}
        </Link>
      )}
      {/* Billing — after schema */}
      {currentApp && renderNavLink({ title: 'Billing', href: `/apps/${currentApp.appId}/billing`, icon: CreditCard })}
    </div>
  );

  return (
    <aside className={cn("flex h-screen flex-col border-r bg-card transition-all duration-200 overflow-hidden", collapsed ? 'w-16' : 'w-64')}>
      {/* Logo */}
      <div className={cn("flex h-16 items-center border-b", collapsed ? 'px-0 justify-center' : 'px-6')}>
        <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-primary-foreground">TC</span>
          </div>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold whitespace-nowrap">TruContext</span>
              {isAdmin && (
                <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 border-primary/50 text-primary font-normal">
                  Admin
                </Badge>
              )}
            </div>
          )}
        </Link>
      </div>

      {/* Admin platform nav — ABOVE app selector */}
      {isAdmin && (
        <div className={cn("space-y-1 border-b", collapsed ? 'p-2' : 'p-4')}>
          {!collapsed && (
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 px-1 font-semibold">
              Platform
            </p>
          )}
          {adminPlatformNav.map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/admin' && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.href}
                to={item.href}
                title={collapsed ? item.title : undefined}
                className={cn(
                  'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
                  collapsed ? 'justify-center px-0' : 'space-x-3 px-3',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{item.title}</span>}
              </Link>
            );
          })}
        </div>
      )}

      {/* App Selector — shared between admin and tenant */}
      {renderAppSelector()}

      {/* App-context navigation — SAME for admin and tenant */}
      {currentApp && needsOnboarding && renderOnboardingNav()}
      {currentApp && appIsComplete && renderFullAppNav()}
      {!currentApp && !collapsed && (
        <div className="p-4">
          <p className="text-xs text-muted-foreground px-3 py-2">
            {isAdmin ? 'Select an app to inspect' : 'Select an app'}
          </p>
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Tenant-only nav items (Dashboard, Apps, Billing, Settings, Docs) */}
      {!isAdmin && appIsComplete && (
        <div className={cn("border-t space-y-1", collapsed ? 'p-2' : 'p-4')}>
          {tenantNavItems.map(item => renderNavLink(item))}
        </div>
      )}

      {/* Collapse Toggle */}
      <div className={cn("border-t", collapsed ? 'p-2' : 'px-4 py-2')}>
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'default'}
          className={cn("text-muted-foreground", collapsed ? 'w-10 h-10 mx-auto flex' : 'w-full justify-start')}
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <><PanelLeftClose className="mr-2 h-5 w-5" />Collapse</>}
        </Button>
      </div>

      {/* Sign Out */}
      <div className={cn("border-t", collapsed ? 'p-2' : 'p-4')}>
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'default'}
          className={cn("text-muted-foreground", collapsed ? 'w-10 h-10 mx-auto flex' : 'w-full justify-start')}
          onClick={logout}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut className={cn("h-5 w-5", !collapsed && "mr-2")} />
          {!collapsed && 'Sign Out'}
        </Button>
      </div>
    </aside>
  );
}
